import { Table } from 'proto/protobufs';
import * as React from 'react';
import { FilterTree, mergeFilterTrees } from '../../../components_v2/filter/model/Model';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import Popup from '../../../components_v2/popup/Popup';
import Toolbar from '../../../components_v2/toolbar/Toolbar';
import LassoIcon from '../../../styles/custom-icons/lasso';
import { DefaultButton, DefaultSecondaryButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor } from '../../../styles/global/css/Utils';
import LoaderContext from '../../../styles/global/loader/LoaderContext';
import { Translate } from '../../../styles/global/translate';
import { NoMarginH5, NoMarginP } from '../../authorization/users/Members';
import { ToolbarBox } from '../../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../../import/model';
import { ReactStateType } from '../../map/Map';
import { MapDotType } from '../../map/MapPin';
import { ModalCalendarContent } from '../../map/modalRight/ModalCalendar';
import type { MandatoryModalState, ModalState } from '../../products/model';
import { FlexDiv } from '../../products/style';
import CompaniesAndMapView from '../CompaniesAndMapView';
import { getRawCompanies } from '../data/action';
import { TSP_COMPANIES_LIMIT } from '../../map/modalRight/model';
import { CompanyOrderBy, CompanyParams } from '../model/Model';

export type SortAndDots = {
	sort: boolean,
	dots?: MapDotType[]
}


function companiesToDots(companies: Table): MapDotType[] {
	return companies.row.map(c => ({ id: c.companyId!, name: c.name!, latitude: c.latitude!, longitude: c.longitude! }));
}

function getDefaultBody(filters?: FilterTree, order_bys?: CompanyOrderBy[]): CompanyParams {
	return {
		offset: 0,
		limit: TSP_COMPANIES_LIMIT,
		columns: ['name', 'latitude', 'longitude'],
		new_filters: mergeFilterTrees(filters, {
			and: [
				{ val: { operator: 'not_empty', column: 'latitude', filter_type: 'numeric' } },
				{ val: { operator: 'not_empty', column: 'longitude', filter_type: 'numeric' } }
			]
		}),
		order_bys
	};
}

export default function CompanyListingPopup(props: {
	modalStateHook: ReactStateType<ModalState<number[]>>,
	onEventCreate?: () => void,
	order_bys: CompanyOrderBy[],
	filters?: FilterTree

}) {
	const [toolBar, setToolBarState] = React.useState<ToolbarState>();
	const [modalState, setModalState] = props.modalStateHook;
	const [body, setBody] = React.useState<CompanyParams>();
	const [optimizationPopupState, setOptimizationPopupState] = React.useState<MandatoryModalState<SortAndDots>>({ isOpen: true, data: { sort: false } });
	const { loaderNotify } = React.useContext(LoaderContext);
	const [allCompanies, setAllCompanies] = React.useState<MapDotType[]>();

	React.useEffect(() => {
		const body = getDefaultBody();
		if (modalState.data && modalState.data.length > 0) {
			body.new_filters = mergeFilterTrees(body.new_filters, { val: { column: 'company_id', value: modalState.data.toString(), operator: 'contained' } });
		}
		getRawCompanies(body).then((companies) => setAllCompanies(companiesToDots(companies)));
	}, [modalState]);

	React.useEffect(() => {
		setOptimizationPopupState({ isOpen: true, data: { sort: false } });
		if (modalState.isOpen) {
			if (props.filters || props.order_bys.length > 0) {
				const body = getDefaultBody(props.filters, props.order_bys);
				if (modalState.data && modalState.data.length > 0) {
					body.new_filters = mergeFilterTrees(body.new_filters, { val: { column: 'company_id', value: modalState.data.toString(), operator: 'contained' } });
				}
				getRawCompanies(body).then((companies) => setOptimizationPopupState({
					isOpen: true,
					data: {
						// Only true when an explicit ordering is in play. Filtering alone
						// is not enough — it narrows the list but doesn't imply rank.
						sort: props.order_bys.length > 0,
						dots: companiesToDots(companies)
					}
				}));
			}
		}
	}, [modalState]);

	const onClose = React.useCallback(() => {
		setModalState({ isOpen: false });
		setOptimizationPopupState({ isOpen: true, data: { sort: false } });
	}, []);

	let content: React.ReactNode;
	if (optimizationPopupState.isOpen) {
		content = <ModalCalendarContent
			allCompanies={allCompanies}
			deleteDots={() => setOptimizationPopupState({ isOpen: true, data: { sort: false } })}
			sortAndDots={optimizationPopupState.data}
			onBack={() => setOptimizationPopupState(p => ({ isOpen: false, data: p.data }))}
			onClose={onClose}
			onEventCreate={props.onEventCreate}
			defaultCompanies={modalState.data}
		/>;
	} else {
		content = <FlexDiv height='100%' width='100%' flow='column' align='start'>
			<Toolbar
				height='50px'
				leftComponent={<NoMarginH5><Translate id='order_your_companies_by_priority' /></NoMarginH5>}
			/>
			<Toolbar
				leftComponent={
					<ToolbarBox>
						{toolBar?.bottomLeftToolbarComponent}
						{(modalState.data?.length ?? 0) > 0 ? <FlexDiv gap='0.5ch'>
							<LassoIcon color={PrimaryColor} />
							<NoMarginP style={{ color: PrimaryColor }}>
								Filtered by lasso
							</NoMarginP>
						</FlexDiv> : undefined}
					</ToolbarBox>
				}
				rightComponent={
					<ToolbarBox>
						<DefaultSecondaryButton
							margin='0'
							onClick={() => setOptimizationPopupState(p => ({ isOpen: true, data: p.data! }))}
						>
							<Translate id='cancel' />
						</DefaultSecondaryButton>
						<DefaultButton
							disabled={!body}
							margin='0'
							onClick={async() => {
								loaderNotify('global', LoadingStateEnum.LOADING);
								try {
									const companies = await getRawCompanies({
										...body!, limit: TSP_COMPANIES_LIMIT, offset: 0, columns: ['name', 'latitude', 'longitude'], new_filters: mergeFilterTrees(body?.new_filters, {
											and: [{ val: { operator: 'not_empty', column: 'latitude', filter_type: 'numeric' } }, { val: { operator: 'not_empty', column: 'longitude', filter_type: 'numeric' } }]
										})
									});
									setOptimizationPopupState({
										isOpen: true,
										data: {
											sort: !!body?.order_bys && body.order_bys.length > 0,
											dots: companies.row.map(c => ({ id: c.companyId!, name: c.name!, latitude: c.latitude!, longitude: c.longitude! }))
										}
									});
								} catch (_error) {
									//
								}
								loaderNotify('global', LoadingStateEnum.LOADED);
							}}>
							<Translate id='save' />

						</DefaultButton>
					</ToolbarBox>
				}
			/>
			<CompaniesAndMapView setToolBarState={setToolBarState} listingOnly height='100%' companies={modalState.data} setBody={setBody} />
		</FlexDiv>;

	}

	return <Popup
		isOpen={modalState.isOpen}
		popupMode={PopupMode.Centered}
		popupStyle={{ height: '90vh', width: '90vw', animate: false }}
		onClickOut={onClose}
	>
		{content}
	</Popup>;
}
